import lib from '../utils/lib';
import transformPosition from '../utils/transformPosition';
import MapMarker from './MapMarker';
import MapPositionListener from './MapPositionListener';
import { ID, areEqual } from './MapPosition';
import mapStyles from './mapStyles';

export const defaultProps = {
  fullscreenControl: false,
  zoomControl: false,
  streetViewControl: false,
  panControl: false,
  mapTypeControl: false,
  clickableIcons: false,
  styles: mapStyles,
};

function updateMarker(marker, markers) {
  const newMarkerId = marker[ID];
  const foundMarker = markers.find(m => m[ID] === newMarkerId);
  if (foundMarker) {
    if (!areEqual(foundMarker.getPosition(), marker.position)) {
      foundMarker.setPosition(marker.position);
    }
  }
  return foundMarker;
}

function updateUniqueMarker(marker, markers, markerWasDragged) {
  const [uniqueMarker] = markers;
  if (uniqueMarker) {
    if (!areEqual(uniqueMarker.getPosition(), marker.position)) {
      uniqueMarker.setPosition(marker.position, {
        animate: !markerWasDragged,
      });
    }
  }
  return uniqueMarker;
}

class MapModelWrapper {
  constructor(domElement, { isEditable, ...params }) {
    const props = {
      ...defaultProps,
      ...params,
      center: params.center && transformPosition(params.center),
    };

    this.lib = lib();
    this.map = new this.lib.Map(domElement, props);
    this.isEditable = !!isEditable;
    this.listeners = [];
    this.markers = [];
    this.markerWasDragged = false;
  }

  setCenter(marker) {
    const { length } = this.markers;
    if (marker && length === 1) {
      if (!this.markerWasDragged) {
        this.map.setCenter(transformPosition(marker));
      }
    } else if (length > 1 && !marker) {
      const bounds = new this.lib.LatLngBounds();
      this.markers.forEach(m => {
        bounds.extend(m.position);
      });
      this.map.setCenter(bounds.getCenter());
      this.map.fitBounds(bounds);
    }
    this.setMarkerWasDragged(false);
  }

  setZoom(zoom) {
    this.map.setZoom(zoom);
  }

  onClick(cb) {
    if (this.isEditable) {
      const listener = this.map.addListener(
        'click',
        MapPositionListener(position => {
          this.setMarkerWasDragged(false);
          cb(position);
        }),
      );
      this.listeners.push(listener);
      return listener;
    }
    return null;
  }

  onVisible(cb) {
    this.lib.event.addListenerOnce(this.map, 'tilesloaded', cb);
  }

  onZoomChanged(cb) {
    this.lib.event.addListener(this.map, 'zoom_changed', () => {
      const newZoom = this.map.getZoom();
      cb(newZoom);
    });
  }

  addMarker(marker) {
    if (this.isEditable) {
      const uniqueMarker = updateUniqueMarker(
        marker,
        this.markers,
        this.markerWasDragged,
      );
      if (uniqueMarker) return uniqueMarker;
    } else {
      const updatedMarker = updateMarker(marker, this.markers);
      if (updatedMarker) return updatedMarker;
    }

    const mapMarker = new MapMarker(this, {
      draggable: this.isEditable,
      ...marker,
    });
    this.markers.push(mapMarker);
    return mapMarker;
  }

  setMarkerWasDragged(markerWasDragged = true) {
    this.markerWasDragged = !!markerWasDragged;
  }

  getInstance() {
    return this.map;
  }

  unmount() {
    this.listeners.forEach(listener => this.lib.event.removeListener(listener));
    this.markers.forEach(marker => marker.unmount());
    this.listeners = [];
    this.markers = [];
    delete this.map;
  }
}

export default MapModelWrapper;
